import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const Select = ({ 
  children, 
  value, 
  onValueChange, 
  disabled = false,
  ...props 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const selectRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newValue) => {
    setSelectedValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
    setIsOpen(false);
  };

  return (
    <div ref={selectRef} className="relative" {...props}>
      <button
        type="button"
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          disabled && "cursor-not-allowed opacity-50"
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="block truncate">
          {React.Children.map(children, child => {
            if (React.isValidElement(child) && child.type === SelectContent) {
              return React.Children.map(child.props.children, item => {
                if (React.isValidElement(item) && item.type === SelectItem && item.props.value === selectedValue) {
                  return item.props.children;
                }
                return null;
              });
            }
            return null;
          })}
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
          {React.Children.map(children, child => {
            if (React.isValidElement(child) && child.type === SelectContent) {
              return React.cloneElement(child, {
                onSelect: handleSelect,
                selectedValue
              });
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center justify-between", className)} {...props}>
    {children}
  </div>
));
SelectTrigger.displayName = 'SelectTrigger';

const SelectContent = ({ children, onSelect, selectedValue, ...props }) => (
  <div className="max-h-60 overflow-auto" {...props}>
    {React.Children.map(children, child => {
      if (React.isValidElement(child) && child.type === SelectItem) {
        return React.cloneElement(child, {
          onSelect,
          selectedValue
        });
      }
      return child;
    })}
  </div>
);

const SelectItem = React.forwardRef(({ 
  className, 
  children, 
  value, 
  onSelect, 
  selectedValue,
  ...props 
}, ref) => (
  <div
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
      selectedValue === value && "bg-accent text-accent-foreground",
      className
    )}
    onClick={() => onSelect && onSelect(value)}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      {selectedValue === value && (
        <div className="h-2 w-2 rounded-full bg-current" />
      )}
    </span>
    {children}
  </div>
));
SelectItem.displayName = 'SelectItem';

const SelectValue = ({ placeholder, ...props }) => (
  <span {...props}>{placeholder}</span>
);

export { Select, SelectTrigger, SelectContent, SelectItem, SelectValue };
